import React from 'react';
import PropTypes from 'prop-types';

import {createTheme, ThemeProvider} from "@mui/material/styles";

import Icon from "./components/Icon";
import Indicator from "./components/Indicator";
import Label from "./components/Label";
import BadgeContainer from "./components/BadgeContainer";

import {
    black500,
    blue50,
    blue100,
    blueColor,
    green50,
    green100,
    green500,
    green600,
    grey300,
    greyishBlue100,
    greyishBlue200,
    greyishBlue50,
    greyishBlue500,
    greyishBlue700,
    orange50,
    orange100,
    orange500,
    orange600,
    purple700,
    purple50,
    purple100,
    red50,
    red100,
    red500,
    red600,
    grey500,
    yellow50,
    yellow700,
    yellow100,
} from "assets/jss/main";

import {BadgeContext} from "./BadgeContext";

Badge.propTypes = {
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number
    ]),
    themeExtension: PropTypes.object,
    color: PropTypes.oneOf([
        'default',
        'success',
        'online',
        'warning',
        'warningContained',
        'error',
        'errorContained',
        'archived',
        'draft',
        'outline',
        'info',
        'oceanBlue',
        'darker',
        'yellow'
    ]),
    size: PropTypes.oneOf([
        'default',
        'small',
        'medium',
        'large',
        'xl'
    ]),
    rounded: PropTypes.oneOf([
        'soft',
        false, // legacy support
        true // legacy support
    ]),
    icon: PropTypes.oneOfType([
        PropTypes.bool, // legacy support, renders before the label
        PropTypes.oneOf(['start', 'end'])
    ]),
    iconSize: PropTypes.number,
    indicator: PropTypes.oneOf([
        'square',
        'circle',
        false, // legacy support
        true // legacy support
    ]),
    indicatorIcon: PropTypes.any,
    backgroundColor: PropTypes.string,
    indicatorColor: PropTypes.string,
    iconColor: PropTypes.string,
    labelColor: PropTypes.string,
    labelSize: PropTypes.number,
    border: PropTypes.bool,
    borderColor: PropTypes.string,
    onClick: PropTypes.func,
    fullWidth: PropTypes.bool
};
// TODO: change "main" by "color" and "light" by background
// TODO: add key to include darker text like green700 instead of green600 (use in dashboard and oms order list)
// it would be a great idea to define this as global app theme
const theme = createTheme({
    palette: {
        default: {
            main: blueColor,
            light: blue50,
        },
        success: {
            main: green50,
            light: green600
        },
        online: {
            main: green500,
            light: green50
        },
        warning: {
            main: orange500,
            light: orange50,
        },
        warningContained: {
            main: orange50,
            light: orange600
        },
        error: {
            main: red500,
            light: red50,
        },
        errorContained: {
            main: red50,
            light: red600,
        },
        archived: {
            main: greyishBlue700,
            light: greyishBlue100,
        },
        draft: {
            main: black500,
            light: grey300
        },
        outline: {
            main: greyishBlue700,
            light: '#FFFFFF'
        },
        info: {
            main: greyishBlue700,
            light: greyishBlue50
        },
        oceanBlue: {
            main: '#FFFFFF',
            light: blueColor,
        },
        darker: {
            main: '#FFFFFF',
            light: greyishBlue500
        },
        yellow: {
            main: yellow700,
            light: yellow50
        },
        purple: {
            main: purple700,
            light: purple50
        }
    },
    padding: {
        default: '2px 8px',
        small: '0px 4px',
        medium: '8px',
        large: '12px',
        xl: '16px',
        noLabel: {
            default: '6px',
            large: '8px',
            hasIndicator: {
                default: '2px',
                large: '8px'
            },
        },
    },
    radius: {
        default: '4px',
        soft: '8px',
        rounded: '48px'
    },
    border: {
        info: {
            main: greyishBlue100,
        },
        outline: {
            main: greyishBlue100,
        },
        archived: {
            main: greyishBlue200,
        },
        online: {
            main: green100
        },
        warning: {
            main: orange100
        },
        error: {
            main: red100
        },
        default: {
            main: blue100
        },
        draft: {
            main: grey500
        },
        yellow: {
            main: yellow100
        },
        purple: {
            main: purple100
        }
    }
});

function Badge({
    label = null,
    color = 'default',
    size = 'default',
    rounded = false,
    icon = false,
    iconSize = 16,
    indicator = null,
    indicatorIcon= null,
    backgroundColor = null,
    indicatorColor = null,
    iconColor = null,
    labelColor = null,
    labelSize = 12,
    isBold = true,
    border = false,
    borderColor = null,
    onClick = null,
    themeExtension = null,
    fullWidth = false,
    centerContent = false,
    ...props
}) {
    const resolvedTheme = themeExtension ? createTheme(theme, themeExtension) : theme;

    return (
        <ThemeProvider theme={resolvedTheme}>
            <BadgeContext.Provider value={{
                hasLabel: !!label || !!props.children,
                rounded,
                color,
                size,
                icon,
                iconSize,
                indicator,
                indicatorIcon,
                backgroundColor,
                indicatorColor,
                iconColor,
                labelColor,
                isBold,
                labelSize,
                border,
                borderColor,
                onClick,
                fullWidth,
                centerContent
            }}>
                <BadgeContainer>
                    {indicatorIcon ? indicatorIcon : indicator && <Indicator />}
                    {icon && 'end' !== icon && <Icon />}
                    {(null !== label || null !== props.children) &&(
                        <Label label={label}>{props.children}</Label>
                    )}
                    {'end' === icon && <Icon />}
                </BadgeContainer>
            </BadgeContext.Provider>
        </ThemeProvider>
    );
}

export default Badge;
